import React, { useState, useEffect, useMemo } from 'react'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import Card from '@material-ui/core/Card'
import Grid from '@material-ui/core/Grid'
import FormGroup from '@material-ui/core/FormGroup'
import FormControl from '@material-ui/core/FormControl'
import Select from '@material-ui/core/Select'
import MenuItem from '@material-ui/core/MenuItem'
import Tooltip from '@material-ui/core/Tooltip'
import ReactBSAlert from 'react-bootstrap-sweetalert'
import BootstrapTable from 'react-bootstrap-table-next'
import Loader from 'components/general/Loader'
import AdminAPI from 'config/api/AdminAPI'
import { onError } from 'config/lib/errorLib'
import ExpenseModal, { PROJECTS, TYPES } from 'components/admin/ExpenseModal'
import ExpensesInsights from 'components/admin/ExpensesInsights'
import moment from 'moment-timezone'

const months = [
    'January', 'February', 'March', 'April',
    'May', 'June', 'July', 'August',
    'September', 'October', 'November', 'December'
]

function buildPeriods() {
    const arr = []
    for (let i = 11; i >= 0; i--) {
        const m = moment().subtract(i, 'months')
        arr.push({
            value: m.format('YYYY-MM'),
            label: `${months[m.month()]} ${m.format('YYYY')}`
        })
    }
    // optional: include next month too, in case of pre-entry
    const next = moment().add(1, 'month')
    arr.push({
        value: next.format('YYYY-MM'),
        label: `${months[next.month()]} ${next.format('YYYY')}`
    })
    return arr
}

const TYPE_COLORS = {
    Wages: { bg: '#e7f5ff', fg: '#1c6dd0' },
    Contractor: { bg: '#fff4e6', fg: '#d9480f' },
    Operating: { bg: '#f3f0ff', fg: '#5f3dc4' }
}

function fmtMoney(amount) {
    return '€' + (Math.round((amount || 0) * 100) / 100).toLocaleString('en-IE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function ExpensesTab(props) {
    const api = new AdminAPI()

    const periods = useMemo(() => buildPeriods(), [])
    const [month, setMonth] = useState(moment().format('YYYY-MM'))
    const [entries, setEntries] = useState([])
    const [previousEntries, setPreviousEntries] = useState([])
    const [allEntries, setAllEntries] = useState([]) // for autocomplete + last-split lookup
    const [loading, setLoading] = useState(true)
    const [showModal, setShowModal] = useState(false)
    const [editing, setEditing] = useState(null)
    const [confirm, setConfirm] = useState(null)

    useEffect(() => {
        loadAll()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        loadMonth(month)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [month])

    async function loadAll() {
        try {
            const all = await api.listExpenses()
            setAllEntries(all || [])
        } catch (e) {
            onError(e)
        }
    }

    async function loadMonth(targetMonth) {
        setLoading(true)
        try {
            const prevMonth = moment(targetMonth, 'YYYY-MM').subtract(1, 'month').format('YYYY-MM')
            const [current, prev] = await Promise.all([
                api.listExpensesByMonth(targetMonth),
                api.listExpensesByMonth(prevMonth)
            ])
            setEntries(current || [])
            setPreviousEntries(prev || [])
        } catch (e) {
            onError(e)
        } finally {
            setLoading(false)
        }
    }

    function openAdd() {
        setEditing(null)
        setShowModal(true)
    }

    function openEdit(row) {
        setEditing(row)
        setShowModal(true)
    }

    function onSaved(saved, mode) {
        // refresh current month and full list
        loadMonth(month)
        loadAll()
        if (mode === 'add') {
            props.showSuccessAlert && props.showSuccessAlert('Expense added')
        } else if (mode === 'delete') {
            props.showSuccessAlert && props.showSuccessAlert('Expense deleted')
        } else {
            props.showSuccessAlert && props.showSuccessAlert('Expense updated')
        }
    }

    function confirmDuplicate() {
        const prevMonth = moment(month, 'YYYY-MM').subtract(1, 'month').format('YYYY-MM')
        const sourceCount = previousEntries.length
        if (sourceCount === 0) {
            setConfirm(
                <ReactBSAlert
                    info
                    style={{ display: 'block', marginTop: '-100px' }}
                    title='Nothing to duplicate'
                    onConfirm={() => setConfirm(null)}
                    confirmBtnBsStyle='primary'
                    confirmBtnText='Ok'
                >
                    No entries found in {prevMonth}.
                </ReactBSAlert>
            )
            return
        }
        setConfirm(
            <ReactBSAlert
                warning
                style={{ display: 'block', marginTop: '-100px' }}
                title='Duplicate previous month?'
                onConfirm={() => doDuplicate(prevMonth)}
                onCancel={() => setConfirm(null)}
                confirmBtnBsStyle='primary'
                cancelBtnBsStyle='secondary'
                confirmBtnText='Duplicate'
                cancelBtnText='Cancel'
                showCancel
            >
                Copy {sourceCount} {sourceCount === 1 ? 'entry' : 'entries'} from {prevMonth} into {month}?
            </ReactBSAlert>
        )
    }

    async function doDuplicate(fromMonth) {
        setConfirm(null)
        setLoading(true)
        try {
            await api.copyExpensesFromMonth(fromMonth, month)
            await Promise.all([loadMonth(month), loadAll()])
            props.showSuccessAlert && props.showSuccessAlert('Previous month duplicated')
        } catch (e) {
            onError(e)
        } finally {
            setLoading(false)
        }
    }

    const totalAmount = useMemo(() => entries.reduce((s, e) => s + (e.amount || 0), 0), [entries])

    const columns = [
        {
            dataField: 'name',
            text: 'Name',
            sort: true,
            headerStyle: { width: '14%' },
            formatter: (cell) => (
                <span style={{ fontWeight: 600, color: '#32325d' }}>{cell}</span>
            )
        },
        {
            dataField: 'description',
            text: 'Description',
            sort: false,
            formatter: (cell) => (
                <span style={{ color: '#525f7f' }}>{cell || '—'}</span>
            )
        },
        {
            dataField: 'type',
            text: 'Type',
            sort: true,
            headerStyle: { width: '110px' },
            formatter: (cell) => {
                const c = TYPE_COLORS[cell] || { bg: '#eee', fg: '#333' }
                return (
                    <span style={{
                        display: 'inline-block',
                        padding: '2px 8px',
                        borderRadius: '12px',
                        background: c.bg,
                        color: c.fg,
                        fontSize: '0.75rem',
                        fontWeight: 600
                    }}>{cell}</span>
                )
            }
        },
        {
            dataField: 'category',
            text: 'Category',
            sort: true,
            formatter: (cell) => (
                <span style={{ color: '#525f7f' }}>{cell}</span>
            )
        },
        {
            dataField: 'payer',
            text: 'Payer',
            sort: true,
            headerStyle: { width: '90px' },
            formatter: (cell) => (
                <span style={{ color: '#525f7f', fontWeight: 500 }}>{cell}</span>
            )
        },
        {
            dataField: 'amount',
            text: 'Amount',
            sort: true,
            headerStyle: { width: '110px', textAlign: 'right' },
            align: 'right',
            formatter: (cell) => (
                <span style={{ color: '#32325d', fontWeight: 600 }}>{fmtMoney(cell)}</span>
            )
        },
        {
            dataField: 'split',
            text: 'Split',
            formatter: (cell, row) => {
                if (!cell) return ''
                const amount = row.amount || 0
                const parts = PROJECTS.filter((p) => (parseFloat(cell[p]) || 0) > 0)
                if (parts.length === 0) return ''
                return (
                    <span style={{ fontSize: '0.8rem', color: '#525f7f' }}>
                        {parts.map((p, i) => {
                            const pct = parseFloat(cell[p]) || 0
                            const euro = (amount * pct) / 100
                            return (
                                <React.Fragment key={p}>
                                    {i > 0 && ' · '}
                                    <Tooltip title={fmtMoney(euro)} arrow>
                                        <span style={{ cursor: 'help' }}>{`${p} ${cell[p]}`}</span>
                                    </Tooltip>
                                </React.Fragment>
                            )
                        })}
                    </span>
                )
            }
        },
        {
            dataField: 'notes',
            text: 'Notes',
            headerStyle: { width: '160px' },
            formatter: (cell) => {
                if (!cell) return ''
                const truncated = cell.length > 40 ? cell.slice(0, 40) + '…' : cell
                return (
                    <Tooltip title={cell}>
                        <span style={{ fontSize: '0.8rem', color: '#8898aa' }}>{truncated}</span>
                    </Tooltip>
                )
            }
        }
    ]

    const rowEvents = {
        onClick: (e, row) => {
            openEdit(row)
        }
    }

    return (
        <Box>
            {confirm}
            <Grid container spacing={2} alignItems='center' style={{ marginBottom: '1.5rem' }}>
                <Grid item xs={12} md={3}>
                    <FormGroup style={{ marginBottom: 0 }}>
                        <FormControl variant='outlined' fullWidth>
                            <Select value={month} onChange={(e) => setMonth(e.target.value)}>
                                {periods.map((p) => (
                                    <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </FormGroup>
                </Grid>
                <Grid item xs={12} md={9}>
                    <Box display='flex' justifyContent='flex-end'>
                        <Button
                            variant='outlined'
                            color='primary'
                            onClick={() => confirmDuplicate()}
                            style={{ marginRight: '0.5rem' }}
                            disabled={loading}
                        >
                            Duplicate Previous Month
                        </Button>
                        <Button
                            variant='contained'
                            color='primary'
                            onClick={() => openAdd()}
                            disabled={loading}
                        >
                            Add Expense
                        </Button>
                    </Box>
                </Grid>
            </Grid>

            {loading ? (
                <Box display='flex' justifyContent='center' alignItems='center' style={{ height: '20vh' }}>
                    <Loader />
                </Box>
            ) : (
                <>
                    <Box marginBottom='1.5rem'>
                        <ExpensesInsights entries={entries} previousEntries={previousEntries} />
                    </Box>
                    <Card>
                        <Box
                            display='flex'
                            alignItems='center'
                            justifyContent='space-between'
                            padding='1rem 1.5rem'
                            borderBottom='1px solid #e9ecef'
                        >
                            <Box component='h3' margin={0} fontSize='1rem' color='#32325d' fontWeight={600}>
                                Entries — {month}
                            </Box>
                            <Box fontSize='0.875rem' color='#8898aa'>
                                {entries.length} {entries.length === 1 ? 'entry' : 'entries'} · <strong style={{ color: '#32325d' }}>{fmtMoney(totalAmount)}</strong>
                            </Box>
                        </Box>
                        <div className='table-container expenses-table'>
                            <BootstrapTable
                                keyField='id'
                                data={entries}
                                columns={columns}
                                rowEvents={rowEvents}
                                rowStyle={{ cursor: 'pointer' }}
                                noDataIndication={
                                    <Box padding='2rem' textAlign='center' color='#8898aa'>
                                        No expenses for {month}. Add one or duplicate from previous month.
                                    </Box>
                                }
                                bordered={false}
                                hover
                            />
                        </div>
                    </Card>
                </>
            )}

            {showModal && (
                <ExpenseModal
                    isOpen={showModal}
                    close={() => setShowModal(false)}
                    expense={editing}
                    month={month}
                    knownEntries={allEntries}
                    onSaved={onSaved}
                />
            )}
        </Box>
    )
}

export default ExpensesTab
